export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...(localStorage.getItem('mailbots_organization') ? { 'x-mailbots-organization': localStorage.getItem('mailbots_organization')! } : {}),
      ...init?.headers,
    },
  });
  if (response.status === 204) return null as T;
  const body = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);
  return body;
}

export const post = <T>(path: string, value: unknown = {}) => api<T>(path, { method: 'POST', body: JSON.stringify(value) });
export const patch = <T>(path: string, value: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(value) });
export const remove = <T>(path: string) => api<T>(path, { method: 'DELETE' });

const REF_KEY = 'mailbots_ref';

// First-touch attribution: the campaign that first brought someone is the one credited
// for what they do later, so an existing value is never overwritten. Storage throws in
// a private window and can come back empty, and an unattributed visitor is a normal
// outcome, not an error.
function storedRef(): string {
  try { return localStorage.getItem(REF_KEY) || ''; } catch { return ''; }
}

// Fire-and-forget POST. It must never block a CTA or throw into a click handler, so
// every failure is swallowed.
function send(payload: Record<string, string>): void {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon?.('/api/events', new Blob([body], { type: 'application/json' }))) return;
    void fetch('/api/events', { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* analytics is never worth breaking navigation for */ }
}

// Conversion counter (SEO-013). `page` is passed explicitly on the app host, where
// location.pathname is not a public route.
export function track(event: string, placement: string, page: string = location.pathname): void {
  const ref = storedRef();
  send({ event, placement, page, ...(ref ? { ref } : {}) });
}

// Records the campaign behind a ?ref= landing, then strips the parameter so it is not
// copied around or shared. Every arrival counts as a visit for the campaign in the URL,
// while later conversions are credited to the first campaign seen — traffic and credit
// answer different questions.
export function captureReferral(): void {
  try {
    const url = new URL(location.href);
    const ref = url.searchParams.get('ref');
    if (!ref) return;
    send({ event: 'visit', ref });
    if (!storedRef()) {
      try { localStorage.setItem(REF_KEY, ref); } catch { /* private window */ }
    }
    url.searchParams.delete('ref');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch { /* a broken URL must not stop the page rendering */ }
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** index).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}

export function formatDate(value?: number | null): string {
  if (!value) return '—';
  const milliseconds = value > 10_000_000_000 ? value : value * 1000;
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(milliseconds));
}
